import type AkAvatar from '@atlaskit/avatar';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import PersonIcon from '@atlaskit/icon/core/person';
import { type BackgroundColor, Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type ComponentProps, type ComponentType, useState } from 'react';

const badgeIconStyles = {
	position: 'absolute',
	right: token('space.0'),
	bottom: token('space.0'),
	width: token('space.300'),
	height: token('space.300'),
	zIndex: 'flag',
	borderRadius: token('radius.full'),
	alignItems: 'center',
	justifyContent: 'center',
} as const;

export type AvatarProps = {
	src?: string;
	alt?: string;
	size?: number;
	badgeIcon?: {
		icon: ComponentType<NewCoreIconProps>;
		color: BackgroundColor;
		label: string;
	};
} & Omit<ComponentProps<typeof AkAvatar>, 'size'>;

export const Avatar = ({ src, alt, size = 54, badgeIcon }: AvatarProps): JSX.Element => {
	const [imageError, setImageError] = useState(false);
	const { icon: BadgeIcon, label } = badgeIcon || {};
	return (
		<div style={{ position: 'relative', width: 'fit-content' }}>
			<Box
				xcss={xcss({
					width: `${size}px`,
					height: `${size}px`,
					borderRadius: 'radius.full',
					overflow: 'hidden',
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'color.background.accent.gray.subtler',
				})}
			>
				{src && !imageError ? (
					<img
						src={src}
						alt={alt || ''}
						onError={() => setImageError(true)}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
						}}
					/>
				) : (
					<Box
						xcss={xcss({
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							scale: 1.5,
						})}
					>
						<PersonIcon label={alt || 'Default avatar'} />
					</Box>
				)}
			</Box>
			<Flex
				xcss={xcss({
					...badgeIconStyles,
					...(badgeIcon?.color && {
						backgroundColor: badgeIcon.color,
					}),
				})}
			>
				{BadgeIcon && (
					<Box
						xcss={xcss({
							paddingTop: 'space.025',
							scale: 0.75,
						})}
					>
						<BadgeIcon label={label as string} color={token('color.text.inverse')} />
					</Box>
				)}
			</Flex>
		</div>
	);
};
